import React, { useCallback, useEffect, useMemo, useState } from "react";
import DataTable from "../../components/table/data-table";
import { InviteModal, SuccessModal } from "../../components/modal";
import { useNavigate } from "react-router-dom";
import axios from "../../api/httpClient";
import { getApiBaseUrl } from "../../data/authStorage";
import FetchLoadingState from "../../components/feedback/FetchLoadingState";
import PageIntroNav from "../../components/navigation/PageIntroNav";
import { readSessionCache, writeSessionCache } from "../../utils/sessionCache";

const BRANCHES_CACHE_KEY = "cehr_company_branches_v1";

const Branches = () => {
  const cachedData = useMemo(
    () => readSessionCache(BRANCHES_CACHE_KEY, { branches: [] }),
    [],
  );
  const [openBranchModal, setOpenBranchModal] = useState(false);
  const [opensucesssModal, setOpensuccessModal] = useState(false);
  const [branches, setBranches] = useState(cachedData?.branches || []);
  const [branchName, setBranchName] = useState("");
  const [branchAddress, setBranchAddress] = useState("");
  const [status, setStatus] = useState("Active");
  const [isLoading, setIsLoading] = useState(!cachedData?.branches?.length);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const openModal = () => setOpenBranchModal(true);
  const apiBaseUrl = getApiBaseUrl();

  const loadBranches = useCallback(
    async ({ silent = false } = {}) => {
      if (!apiBaseUrl) {
        setErrorMessage("API base URL is not configured.");
        return;
      }

      try {
        if (!silent && branches.length === 0) {
          setIsLoading(true);
        }
        setErrorMessage("");

        const response = await axios.get(
          `${apiBaseUrl}/organizations/branches`,
        );
        const nextBranches = response?.data?.branches || [];
        setBranches(nextBranches);
        writeSessionCache(BRANCHES_CACHE_KEY, { branches: nextBranches });
      } catch (error) {
        const responseMessage =
          error?.response?.data?.message ||
          "Unable to load branches. Please try again.";
        setErrorMessage(
          Array.isArray(responseMessage)
            ? responseMessage.join(", ")
            : responseMessage,
        );
      } finally {
        setIsLoading(false);
      }
    },
    [apiBaseUrl, branches.length],
  );

  useEffect(() => {
    loadBranches({ silent: true });
  }, [loadBranches]);

  useEffect(() => {
    const handleFocusRefresh = () => {
      if (document.visibilityState !== "hidden") {
        loadBranches({ silent: true });
      }
    };

    window.addEventListener("focus", handleFocusRefresh);
    document.addEventListener("visibilitychange", handleFocusRefresh);

    return () => {
      window.removeEventListener("focus", handleFocusRefresh);
      document.removeEventListener("visibilitychange", handleFocusRefresh);
    };
  }, [loadBranches]);

  const resetForm = () => {
    setBranchName("");
    setBranchAddress("");
    setStatus("Active");
    setErrorMessage("");
  };

  const closeBranchModal = () => {
    setOpenBranchModal(false);
    resetForm();
  };

  const createHandleSuccess = async (ev) => {
    ev.preventDefault();

    if (!apiBaseUrl) {
      setErrorMessage("API base URL is not configured.");
      return;
    }

    if (!branchName.trim() || !branchAddress.trim() || !status.trim()) {
      setErrorMessage("Branch name, branch address and status are required.");
      return;
    }

    try {
      setIsSubmitting(true);
      setErrorMessage("");

      await axios.post(`${apiBaseUrl}/organizations/branches`, {
        branchName: branchName.trim(),
        branchAddress: branchAddress.trim(),
        status: status.trim(),
      });

      closeBranchModal();
      setOpensuccessModal(true);
      await loadBranches();
    } catch (error) {
      const responseMessage =
        error?.response?.data?.message ||
        "Unable to create branch. Please try again.";
      setErrorMessage(
        Array.isArray(responseMessage)
          ? responseMessage.join(", ")
          : responseMessage,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  let navigate = useNavigate();
  const headings = ["Branch Name", "Branch Address", "Status", "Actions"];
  const columns = ["branchName", "branchAddress", "status"];
  return (
    <div className="p-6">
      <PageIntroNav
        breadcrumbs={[
          { label: "Company Settings", path: "/company-settings" },
          { label: "Branches" },
        ]}
        title="Branches"
        subtitle="Manage branch locations and status."
        fallbackPath="/company-settings"
        backLabel="Back to Company Settings"
        actions={
          <button
            onClick={openModal}
            className="bg-[#1234CD] px-6 py-2 rounded-xl text-white"
          >
            Add new
          </button>
        }
      />

      <DataTable
        title=""
        headings={headings}
        columns={columns}
        data={branches}
        showEmptyState={!isLoading}
        emptyMessage="No branches found. Create one to get started."
        onView={(row) => {
          sessionStorage.setItem("selectedBranchId", row.id);
          navigate("/company-settings/branchview-details", {
            state: { branchId: row.id },
          });
        }}
      />

      <InviteModal
        isOpen={openBranchModal}
        onClose={closeBranchModal}
      >
        <div className="my-8">
          <div>
            <p className=" text-2xl font-semibold text-black font-Nunito">
              Create Branch
            </p>
          </div>
          <form
            className="my-12"
            onSubmit={createHandleSuccess}
          >
            {errorMessage && (
              <p
                className="mb-4 text-sm text-red-600"
                role="alert"
              >
                {errorMessage}
              </p>
            )}

            <div>
              <label
                htmlFor="branch-name"
                className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px] font-Nunito"
              >
                Branch Name
              </label>
              <input
                type="text"
                id="branch-name"
                placeholder="Title"
                value={branchName}
                onChange={(ev) => setBranchName(ev.target.value)}
                className="w-full p-2 text-center mb-2  rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5] py-[16px]"
              />
            </div>

            <div>
              <label
                htmlFor="branch-address"
                className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px] font-Nunito"
              >
                Branch Address
              </label>
              <input
                type="text"
                id="branch-address"
                placeholder="Address"
                value={branchAddress}
                onChange={(ev) => setBranchAddress(ev.target.value)}
                className="w-full p-2 text-center mb-2  rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5] py-[16px]"
              />
            </div>

            <div>
              <label
                htmlFor="branch-status"
                className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px] font-Nunito"
              >
                Status
              </label>
              <select
                id="branch-status"
                value={status}
                onChange={(ev) => setStatus(ev.target.value)}
                className="w-full p-2 text-center mb-2  rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5] py-[16px]"
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>

            <div className=" flex justify-center mt-10 items-end">
              <button
                type="submit"
                disabled={isSubmitting}
                className="font-[700] font-Nunito  leading-[21.6px] w-40 rounded-xl text-white py-[16px] bg-[#1234CD]  h-[40px]text-[16px] disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <span>{isSubmitting ? "Creating..." : "Create"}</span>
              </button>
            </div>
          </form>
        </div>
      </InviteModal>

      <SuccessModal
        isOpen={opensucesssModal}
        onClose={() => setOpensuccessModal(false)}
      />

      {isLoading && branches.length === 0 ? (
        <FetchLoadingState message="Loading branches..." />
      ) : null}
    </div>
  );
};

export default Branches;
